import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { EditTemplatesComponent } from '@components/traffic-deviations/components/edit-templates/edit-templates.component';
import { TranslateModule } from '@ngx-translate/core';
import { APP_CONTEXT } from '@modules/core/models/app-state';
import { InformationTemplate, TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { BehaviorSubject, firstValueFrom, map, Subject, take, takeUntil } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { AppStateService } from '@modules/core/services/app-state.service';
import { ConfirmationDialogData } from '@modules/shared/dialogs';
import { TranslateService } from '@modules/core/services';
import { DialogService } from '@modules/shared/services/dialog.service';
import { TrafficDeviationTypesUnion } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-union';
import { FormControl } from '@angular/forms';

@Component({
    selector: 'app-edit-traffic-deviation-templates-overview',
    imports: [CommonModule, AsyncPipe, TranslateModule, EditTemplatesComponent],
    templateUrl: './edit-traffic-deviation-templates-overview.component.html',
    styleUrls: ['./edit-traffic-deviation-templates-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditTrafficDeviationTemplatesOverviewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren('templateCheckbox') templateCheckboxes: ElementRef<HTMLInputElement>[];
  @ViewChild(EditTemplatesComponent) editTemplatesComponent: EditTemplatesComponent;
  @ViewChild('automaticTemplatesWrapper') automaticTemplatesWrapper: ElementRef<HTMLInputElement>;

  private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
  private readonly _router = inject(Router);
  private readonly _appStateService = inject(AppStateService);
  private readonly _translateService = inject(TranslateService)
  private readonly _dialogService = inject(DialogService);

  public readonly menuWidth = 392;  

  protected templates$ = this._trafficDeviationsStore.informationTemplates$.pipe(
    map((templates) => templates.filter((template) => !template.automaticTemplate))
  );
  protected automaticTemplates$ = this._trafficDeviationsStore.informationTemplates$.pipe(
    map((templates) => templates.filter((template) => template.automaticTemplate))
  );
  protected readonly selectedTemplate$ = new BehaviorSubject<InformationTemplate>(undefined);
  protected readonly automaticTemplateWrapper = new FormControl(false);

  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {  
    this._setupSubscriptionHandleSettingsBlade();
  }

  ngAfterViewInit(): void {
    this._handleDefaultTemplate();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public async canDeactivate(): Promise<any> {
    const modalData = new ConfirmationDialogData(
        this._translateService.instant('Warning'),
        this._translateService.instant('If you continue, you will lose your changes'),
        this._translateService.instant('Continue'),
        this._translateService.instant('Cancel'),
        undefined,
        this._translateService.instant('Save and continue')
    );

    return await firstValueFrom(this._dialogService.showConfirmation(modalData));
  }

  protected async onFocusTemplate(template: InformationTemplate): Promise<void> {
    if (template.id === this.selectedTemplate$.getValue()?.id) return;
    await this.selectTemplate(template);
  }

  protected onClickAutomaticTemplateWrapper(): void {
    this.automaticTemplateWrapper.setValue(!this.automaticTemplateWrapper.value);
  }
  
  protected async onClickNewTemplate(): Promise<void> {
    if (await this._checkIsDirty()) return;
    
    this.selectedTemplate$.next({} as InformationTemplate);
  }

  protected async selectTemplate(template: InformationTemplate): Promise<void> {
    if (await this._checkIsDirty()) return;

    if (template.id === this.editTemplatesComponent.selectedTemplateId) {
      this.editTemplatesComponent.selectedTemplateId = '';
    }
    this.selectedTemplate$.next({...template});
    const selectedCheckbox = this.templateCheckboxes?.find((checkbox) => {
      return checkbox.nativeElement.id === template.id;
    });

    if (selectedCheckbox) selectedCheckbox.nativeElement.focus();
  }

  protected onAutomaticWrapperMarkPrevious(event: Event): void {
    // stops scroll on the page when pressing the arrow key
    event.preventDefault();

    this.templates$.pipe(take(1)).subscribe((templates) => {
      if (!templates.length) return;
      this.selectTemplate(templates[templates.length-1]);
    });
  }

  protected onAutomaticWrapperMarkNext(event: Event): void {
    // stops scroll on the page when pressing the arrow key
    event.preventDefault();

    if (this.automaticTemplateWrapper.value) {    
      this.automaticTemplates$.pipe(take(1)).subscribe((templates) => {
        if (!templates.length) return;
        this.selectTemplate(templates[0]);
      });
      return;
    }
    
    this.templates$.pipe(take(1)).subscribe((templates) => {
      if (!templates.length) return;
      this.selectTemplate(templates[0]);
    });
  }

  protected async onAutomaticMarkPrevious(event: Event): Promise<void> {
    event.preventDefault();

    const automaticTemplates = await firstValueFrom(this.automaticTemplates$);
    const selectedTemplate = await firstValueFrom(this.selectedTemplate$);
    if (this.automaticTemplateWrapper.value) {   
        const selectedTemplateIndex = automaticTemplates.findIndex((x) => {
          return x.id === selectedTemplate.id;
        });
        if (selectedTemplateIndex === -1) return;
        if (selectedTemplateIndex === 0) {
          this.automaticTemplatesWrapper.nativeElement.focus();
        } else {
          this.selectTemplate(automaticTemplates[selectedTemplateIndex-1]);
        }

      return;
    }

    this.automaticTemplatesWrapper.nativeElement.focus();
  }

  protected async onAutomaticMarkNext(event: Event): Promise<void> {
    event.preventDefault();

    const automaticTemplates = await firstValueFrom(this.automaticTemplates$);
    const selectedTemplate = await firstValueFrom(this.selectedTemplate$);
    const templates = await firstValueFrom(this.templates$);
    if (this.automaticTemplateWrapper.value) {   
        const selectedTemplateIndex = automaticTemplates.findIndex((x) => {
          return x.id === selectedTemplate.id;
        });
        if (selectedTemplateIndex === -1) return;
        if (selectedTemplateIndex === automaticTemplates.length - 1) {
          this.selectTemplate(templates[0]);
        } else {
          this.selectTemplate(automaticTemplates[selectedTemplateIndex+1]);
        }

      return;
    }

    this.selectTemplate(templates[0]);
  }

  protected markPrevious(event: Event): void {
    event.preventDefault();
    this._moveToNextTemplate(false);
  }

  protected markNext(event: Event): void {
    event.preventDefault();
    this._moveToNextTemplate(true);
  }

  private _setupSubscriptionHandleSettingsBlade(): void {
    this._router.events.pipe(
      takeUntil(this._destroy$)
    ).subscribe(x => {
        if (x instanceof NavigationEnd) {
            if (
                this._router.url.includes('settings') 
                && this._appStateService.stateSnapshot?.selectedContext === APP_CONTEXT.EDIT_TRAFFIC_DEVIATION_TEMPLATES
            ) {
                this._appStateService.changeContext(APP_CONTEXT.NONE, false);
            }
        }
    });
  }

  private _handleDefaultTemplate(): void {
    this.templates$.pipe(take(1)).subscribe(async (templates) => {
      if (!templates?.length) return;

      let selectedTemplate: InformationTemplate;

      this._trafficDeviationsStore.savedPreviewEditForm$.pipe(
        take(1) 
      ).subscribe((previewForm) => {
        if (previewForm) {
          selectedTemplate = {
            id: previewForm.id, 
            version: previewForm.version,
            content: previewForm.content,
            header: previewForm.header,
            name: previewForm.name,
            summary: previewForm.summary,
            modifiedBy: previewForm.modifiedBy,
            modifiedDate: previewForm.modifiedDate,
            deviationTypes: Object.entries(previewForm.deviationTypes)
              .filter(([_, value]) => value)
              .map(([key]) => key) as Array<TrafficDeviationTypesUnion['type']>
          };
        }
      });

      if (selectedTemplate) {
        this.selectTemplate(selectedTemplate);
        return;
      }
    
      if (templates.some((template) => template.defaultTemplate)) {
        await this.selectTemplate(templates.find((template) => template.defaultTemplate));
        return;
      }

      await this.selectTemplate(templates[0]); 
    });
  }

  private async _moveToNextTemplate(markNext: boolean): Promise<void> {
    const templates = await firstValueFrom(this.templates$);
    const automaticTemplates = await firstValueFrom(this.automaticTemplates$);

    let selectedIndex = templates.findIndex((template) => {
      return template.id === this.selectedTemplate$.getValue().id;
    });
    if (selectedIndex === -1) return;

    if (selectedIndex === 0 && !markNext) {
      if (this.automaticTemplateWrapper.value && automaticTemplates.length) { 
        this.selectTemplate(automaticTemplates[automaticTemplates.length-1]);  
      }

      this.automaticTemplatesWrapper.nativeElement.focus();
      return;
    } else if (selectedIndex === templates.length - 1 && markNext) {
      this.automaticTemplatesWrapper.nativeElement.focus();
      return;
    } else {
      selectedIndex += markNext ? 1 : -1;
    }
    
    this.selectTemplate(templates[selectedIndex]);
  }

  private async _checkIsDirty(): Promise<boolean> {
    const isDirty = this.editTemplatesComponent.checkIsDirty();
    if (isDirty) {
      const canDeactivate = await this.canDeactivate();
      if (canDeactivate?.saveAndConfirm) {
        const submitResponse = await firstValueFrom(this.editTemplatesComponent.publishTemplate())
        return !submitResponse;
      }

      return !canDeactivate;
    }

    return false;
  }
}